import { type UseInfiniteQueryOptions, type UseInfiniteQueryResult, useInfiniteQuery } from '@tanstack/react-query';
import { compose } from 'ramda';

import { useApplicationContext } from '@/app/shared/context/ApplicationContext';
import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { insertPageToJsonQuery, insertSortToJsonQuery } from '@/packages/JsonQuery';
import {
  filtersSelector,
  listingSortSelector,
  pageSelector,
  useQueryKeySelector,
} from '@/packages/redux-state/store/slices/jsonQuery/selectors/dumb';
import { getCancellationTokenFromSignal } from '@/packages/utils/getCancellationTokenFromSignal';

import { type TSearchOffersDesktopResponse_1 } from '@/repositories/generated/shared/search-offers/v3/search-offers-desktop';

import { type QueryError, type QueryKey, getQueryKey } from './internal/config';
import { fetchSearchOffersDesktopService } from './internal/fetchService';

type UseInfiniteSearchOffersDesktopQueryOptions = UseInfiniteQueryOptions<
  TSearchOffersDesktopResponse_1,
  QueryError,
  TSearchOffersDesktopResponse_1,
  TSearchOffersDesktopResponse_1,
  QueryKey
>;

export function useSearchOffersDesktopQuery(
  options?: UseInfiniteSearchOffersDesktopQueryOptions,
): UseInfiniteQueryResult<TSearchOffersDesktopResponse_1, QueryError> {
  const reduxState = useReduxState();

  const applicationContext = useApplicationContext();

  const queryKey = useQueryKeySelector();

  return useInfiniteQuery<TSearchOffersDesktopResponse_1, QueryError, TSearchOffersDesktopResponse_1, QueryKey>({
    getNextPageParam: data => {
      const currentPage = pageSelector(reduxState.getState());

      const { maxPage, aggregatedOffersCount: totalOffers, offersPerPage } = data.listingProperties;

      if (totalOffers === 0 || data.offers.length === 0) {
        return;
      }

      const totalPages = maxPage || Math.ceil(totalOffers / offersPerPage);

      return totalPages === 1 || currentPage === totalPages ? undefined : currentPage + 1;
    },
    queryFn: ({ signal, pageParam }) => {
      const state = reduxState.getState();
      const nextPage = pageParam || pageSelector(state);
      const sort = listingSortSelector(state);
      const jsonQuery = filtersSelector(state);

      return fetchSearchOffersDesktopService({
        applicationContext,
        cancellationToken: getCancellationTokenFromSignal(signal),
        parameters: { jsonQuery: compose(insertPageToJsonQuery(nextPage), insertSortToJsonQuery(sort))(jsonQuery) },
      });
    },
    queryKey: getQueryKey(queryKey),
    ...options,
  });
}
